import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";

import { useOfficeRealtime } from "../realtime/OfficeRealtimeProvider";

/**
 * The answer to something you asked, delivered wherever you happen to be.
 *
 * `answer` has always emitted `knock.resolved` to whoever asked, carrying the
 * target to open — and nothing listened to it. So accepting connected only the
 * person who accepted: they walked into the call and the asker sat looking at
 * the floor, never told either way. A decline was equally silent.
 *
 * Mounted for the whole shell rather than the module, because the wait is
 * exactly when somebody goes back to their inbox.
 */
export function KnockResolutions() {
  const { subscribe } = useOfficeRealtime();
  const navigate = useNavigate();
  const { t } = useTranslation();

  useEffect(
    () =>
      subscribe((frame) => {
        if (frame.type !== "knock.resolved") return;
        const payload = frame.payload as {
          status: string;
          to_user_name?: string;
          target?: { kind: string; href: string } | null;
        };

        if (payload.status === "accepted") {
          // The server builds the href, so office stays the only thing that
          // decides a knock was accepted; the client only follows it.
          if (payload.target?.href) navigate(payload.target.href);
          return;
        }
        // A decline already creates a notification server-side; this is just
        // so the person who asked is not left waiting on a prompt that has
        // already been answered. An expiry says nothing — nobody refused.
        if (payload.status === "declined") {
          toast.info(
            t("homework.knock.declinedByThem", {
              name: payload.to_user_name ?? t("homework.room.somebody"),
            }),
          );
        }
      }),
    [subscribe, navigate, t],
  );

  return null;
}
